import { em } from 'polished'
import PropTypes from 'prop-types'
import React from 'react'

import TextWithPlus from 'components/_special/TextWithPlus'
import ButtonScrollDown from 'components/ButtonScrollDown'
import List from 'components/List'
import { GOODS_TYPES } from 'constants/sales'
import AddAndGoToCart from 'containers/AddAndGoToCart'
import { LocalizationContext } from 'contexts/Localization'
import { ResizeContext } from 'contexts/Resize'
import { Col, Row } from 'styles/components/layout'
import { H1Small } from 'styles/components/typography'
import { getActiveDiscount } from 'utils/sales/discounts'

import {
  FirstBlockWrapperStyled,
  Header,
  DynamicImageStyled,
  ListStyled,
  ColStyled,
  RowStyled,
  ListWrapper,
  DiscountNameLabel,
  DiscountLabel,
  LabelsWrapper,
  LabelText,
  SalesContainer,
  CostContainerSaleStyled,
  CostWrapper,
  ImageWrapper,
  DiscountLabelRound,
  LabelValue,
  ButtonInner,
  Decorations,
} from './styles'
const { discounts } = process.env.localization

const theme = {
  regular: {
    bg: 'purpleHeart',
    button: 'green',
    textButton: 'white',
    img: 'left-new',
  },
  blackFriday: {
    bg: 'black',
    button: 'white',
    textButton: 'black',
    img: 'left-without-shadow',
    labelBg: 'yellow',
    labelText: 'black',
    markColor: 'grass',
  },
  newYear: {
    bg: 'tePapaGreen',
    button: 'white',
    textButton: 'black',
    img: 'left-without-shadow',
    labelBg: 'monza',
    labelText: 'white',
    decoratingImg: 'wgs_newYear',
    markColor: 'yellow',
  },
}

/**
 * DAboutFirstBlock
 *
 * @param {object} props - properties of component
 * @returns {node} DAboutFirstBlock
 */
const WAboutFirstBlock = ({ data, secondBlockRef }) => {
  const { paths, dict, locale, sales } = React.useContext(LocalizationContext)
  const { mobile } = React.useContext(ResizeContext)

  const discountTheme = React.useMemo(() => {
    const { theme } = getActiveDiscount(discounts, locale) || {}
    return theme
  }, [locale])

  const { discount } = sales?.goods
    ? sales.goods[GOODS_TYPES.wgs]
    : { discount: 0 }

  const listContent = React.useMemo(
    () =>
      mobile
        ? dict.kits[GOODS_TYPES.wgs].list_mob.filter(item => !!item)
        : dict.kits[GOODS_TYPES.wgs].list.filter(item => !!item),
    [mobile, dict],
  )

  const colorTheme = discountTheme ? theme[discountTheme] : theme.regular

  return (
    <FirstBlockWrapperStyled
      bg={colorTheme.bg}
      isExtraBottomIndent={!!discountTheme}
    >
      <RowStyled>
        <ColStyled flexGrow={{ xs: 1 }} textAlign={{ xs: 'center' }}>
          <Header>
            <H1Small dangerouslySetInnerHTML={{ __html: data.title }} />
          </Header>
        </ColStyled>
      </RowStyled>

      <RowStyled>
        <ColStyled
          width={{ xs: 1, md: 6 / 12 }}
          textAlign={{ xs: 'center', md: 'left' }}
        >
          <ImageWrapper>
            <DynamicImageStyled
              alt="wgs-box"
              image={`${paths.cdn_boxes}/wgs-${colorTheme.img}`}
              imageType="png"
              fitOption="contain"
              imageSizes={{ xs: 266, md: 530 }}
            />
            {discount > 0 && !!discountTheme && (
              <DiscountLabelRound
                bg={colorTheme.labelBg}
                textColor={colorTheme.labelText}
              >
                <LabelValue
                  dangerouslySetInnerHTML={{ __html: `-${discount}%` }}
                />
              </DiscountLabelRound>
            )}
          </ImageWrapper>
          {!!colorTheme.decoratingImg && (
            <Decorations>
              <DynamicImageStyled
                image={`${paths.shared}/decorations/${colorTheme.decoratingImg}`}
                imageType="png"
                imageSizes={{ xs: 240, md: 266, lg: 393 }}
                fitOption="contain"
                namesByBreakpoints
                alt={'Decorations'}
              />
            </Decorations>
          )}
        </ColStyled>

        <Col width={{ xs: 1, md: 6 / 12 }}>
          <Row>
            <ColStyled
              width={{ xs: 1 }}
              order={{ xs: discountTheme ? 0 : 1, md: 0 }}
              pt={{ xs: em(discountTheme ? 40 : 20), md: 0 }}
              textAlign={{ xs: 'center', md: 'left' }}
            >
              <ListWrapper>
                <ListStyled
                  className="text-left"
                  textColor="white"
                  leftAlignment="text"
                  markColor={colorTheme.markColor}
                >
                  {listContent.map((item, index) => (
                    <List.Item key={index} bulletColor="white">
                      {item}
                    </List.Item>
                  ))}
                </ListStyled>
                <TextWithPlus
                  text={dict.kits[GOODS_TYPES.wgs].consultation_free}
                />
              </ListWrapper>
            </ColStyled>

            <ColStyled
              width={{ xs: 1 }}
              order={{ xs: discountTheme ? 1 : 0, md: 0 }}
              pt={{ xs: em(32) }}
            >
              <SalesContainer>
                {discountTheme && discount > 0 && (
                  <LabelsWrapper>
                    <DiscountLabel
                      bg={colorTheme.labelBg}
                      textColor={colorTheme.labelText}
                    >
                      <LabelText
                        dangerouslySetInnerHTML={{ __html: `-${discount}%` }}
                      />
                    </DiscountLabel>
                    <DiscountNameLabel>
                      <LabelText
                        dangerouslySetInnerHTML={{
                          __html: dict[discountTheme],
                        }}
                      />
                    </DiscountNameLabel>
                  </LabelsWrapper>
                )}
                <CostWrapper isMobileLeftAlign={!!discountTheme}>
                  <CostContainerSaleStyled
                    productType={GOODS_TYPES.wgs}
                    isWhite={true}
                    crossedSize={mobile ? 'base' : 'medium'}
                    isMobileCenter={!discountTheme}
                  />

                  <AddAndGoToCart
                    goods={[
                      {
                        type: GOODS_TYPES.wgs,
                        isSubscription: false,
                      },
                    ]}
                    buttonProps={{
                      bgClassName: `bg--${colorTheme.button}`,
                      textClassName: `text--${colorTheme.textButton}`,
                      withTopMarginOnMobile: true,
                    }}
                    forceMultipleAdd={true}
                  >
                    <ButtonInner
                      as="span"
                      dangerouslySetInnerHTML={{
                        __html: sales.withoutSales
                          ? dict.buttons.out_of_stock
                          : dict.buttons.buy,
                      }}
                    />
                  </AddAndGoToCart>
                </CostWrapper>
              </SalesContainer>
            </ColStyled>
          </Row>
        </Col>
      </RowStyled>

      {!discountTheme && (
        <Row>
          <ColStyled width={{ xs: 1 }} pt={{ xs: em(40) }}>
            <ButtonScrollDown
              title={data.go_to_site}
              isLightColor={true}
              refToScroll={secondBlockRef}
            />
          </ColStyled>
        </Row>
      )}
    </FirstBlockWrapperStyled>
  )
}

WAboutFirstBlock.propTypes = {
  data: PropTypes.object.isRequired,
  secondBlockRef: PropTypes.object.isRequired,
}

export default WAboutFirstBlock
